/**
 * FormRendererService
 * 
 * Service responsible for dynamically generating HTML content for form views and PDF generation
 * throughout the MacSafety web application. Creates consistent HTML output that can be used
 * both in the frontend UI and sent to the backend for PDF creation.
 * 
 * Key Features:
 * - Dynamic component creation and rendering
 * - HTML generation for form view display
 * - PDF-ready HTML generation for backend processing
 * - Attachment and metadata integration
 * - Form step organization and rendering
 * - Field label and metadata processing
 * 
 * This service bridges the gap between form data and visual representation by:
 * 1. Dynamically creating FormRenderComponent instances
 * 2. Injecting form data, steps, and metadata
 * 3. Triggering component lifecycle and change detection
 * 4. Extracting the rendered HTML for use in UI or PDF generation
 * 
 * Components that utilize this service:
 * - FormViewComponent: For displaying existing form entries
 * - PDF generation services: For creating PDFs with consistent HTML
 * - Print services: For generating print-ready content
 * - Any component requiring form HTML for external use
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Injectable, ComponentRef, ViewContainerRef, ComponentFactoryResolver, ApplicationRef, Injector } from '@angular/core';
import { FormStep } from '../models/forms/form-steps.interface';
import { ResolvedAttachments } from '../models/forms/attachments/resolved-attachments';
import { FormRenderComponent } from '../components/forms/shared/form-view-render/form-view-render.component';
import { FormFieldMetadata } from '../utils/field-type-extractor';
import { WordPressField } from '../models/forms/form-retrieval-response';

/**
 * Interface defining the data required for form HTML generation
 * Contains all necessary information to render a complete form view
 */
export interface FormRenderData {
  formEntry: any;                    // The actual form data to render
  formSteps: FormStep[];             // Form structure and organization
  fieldLabels: Record<string, string>; // Human-readable field labels
  formTitle: string;                 // Display title for the form
  attachments?: ResolvedAttachments; // Optional file attachments
  fieldMetadata?: FormFieldMetadata; // Field type and validation metadata
  wordPressFields?: WordPressField[]; // WordPress-specific field data
}

@Injectable({
  providedIn: 'root'
})
export class FormRendererService {

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private injector: Injector
  ) { }

  /**
   * Generates the complete HTML for a form entry by dynamically creating the component
   * This is the core method that creates a FormRenderComponent, injects data,
   * triggers rendering, and extracts the resulting HTML
   * 
   * @param data - Complete form data including entry, steps, labels, and metadata
   * @returns Promise resolving to the rendered HTML string
   */
  async generateFormHTML(data: FormRenderData): Promise<string> {
    const { formEntry, formSteps, fieldLabels, formTitle, attachments, fieldMetadata, wordPressFields } = data;
    
    if (!formEntry) {
      return '<div class="empty-state">No form entry found.</div>';
    }

    // Create the component factory for FormRenderComponent
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(FormRenderComponent);
    
    // Create a new component instance with the injector
    const componentRef: ComponentRef<FormRenderComponent> = componentFactory.create(this.injector);
    
    try {
      // Set all input properties on the component instance
      componentRef.instance.formEntry = formEntry;
      componentRef.instance.formSteps = formSteps;
      componentRef.instance.fieldLabels = fieldLabels;
      componentRef.instance.formTitle = formTitle;
      componentRef.instance.attachments = attachments;
      componentRef.instance.wordPressFields = wordPressFields || [];
      // Pass field metadata directly to avoid service dependency issues
      if (fieldMetadata) {
        componentRef.instance.fieldMetadata = fieldMetadata;
      }

      // Attach the component to the application reference BEFORE calling lifecycle methods
      // This ensures the component is properly integrated with Angular's change detection
      this.appRef.attachView(componentRef.hostView);

      // Manually trigger the component's initialization lifecycle
      // This ensures all the optimized processing (buildProcessedData, etc.) runs
      if (componentRef.instance.ngOnInit) {
        componentRef.instance.ngOnInit();
      }

      // Trigger change detection to ensure everything is processed and rendered
      componentRef.changeDetectorRef.detectChanges();

      // Wait a tick to ensure all async operations complete
      await this.waitForNextTick();

      // Create a temporary container to extract the HTML content
      const tempContainer = document.createElement('div');
      tempContainer.appendChild(componentRef.location.nativeElement);

      // Extract the rendered HTML content
      const htmlContent = tempContainer.innerHTML;

      return htmlContent;

    } finally {
      // Clean up in finally block to ensure cleanup even if there's an error
      // Detach the component view and destroy the component reference
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();
    }
  }

  /**
   * Generates form HTML without attachments (for placeholder mode)
   * Useful when you need the form structure but don't want to include file attachments
   * 
   * @param data - Form data excluding attachments
   * @returns Promise resolving to the rendered HTML string without attachments
   */
  async generateFormHTMLWithoutAttachments(data: Omit<FormRenderData, 'attachments'>): Promise<string> {
    return this.generateFormHTML({
      ...data,
      attachments: undefined
    });
  }

  /**
   * Generates form HTML with processed attachment metadata
   * This version ensures the component has all the field metadata it needs
   * for proper rendering and PDF generation
   * 
   * @param data - Complete form data including metadata
   * @returns Promise resolving to the rendered HTML string with full metadata
   */
  async generateFormHTMLWithMetadata(data: FormRenderData): Promise<string> {
    const { formEntry, formSteps, fieldLabels, formTitle, attachments } = data;
    
    if (!formEntry) {
      return '<div class="empty-state">No form entry found.</div>';
    }

    // Log for debugging
    // console.log('FormRendererService: Generating HTML with metadata processing');
    // console.log('Form entry type:', formEntry.constructor?.name);
    // console.log('Has attachments:', !!attachments);
    // console.log('Form steps count:', formSteps?.length || 0);

    return this.generateFormHTML(data);
  }

  /**
   * Helper method to wait for next tick
   * Ensures all async operations and change detection cycles complete
   * before extracting HTML content
   * 
   * @returns Promise that resolves after the next tick
   */
  private waitForNextTick(): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, 0));
  }

  /**
   * Helper method to validate form render data
   * Checks that all required data is present and logs warnings for missing data
   * 
   * @param data - The form render data to validate
   * @returns True if data is valid, false otherwise
   */
  private validateFormRenderData(data: FormRenderData): boolean {
    if (!data.formEntry) {
      console.warn('FormRendererService: No form entry provided');
      return false;
    }

    if (!data.fieldLabels || Object.keys(data.fieldLabels).length === 0) {
      console.warn('FormRendererService: No field labels provided');
    }

    if (!data.formSteps || data.formSteps.length === 0) {
      console.warn('FormRendererService: No form steps provided');
    }

    return true;
  }

  /**
   * Generates form HTML with validation
   * Performs data validation before attempting HTML generation
   * Includes error handling and fallback error states
   * 
   * @param data - The form render data to validate and process
   * @returns Promise resolving to rendered HTML or error state HTML
   */
  async generateValidatedFormHTML(data: FormRenderData): Promise<string> {
    if (!this.validateFormRenderData(data)) {
      return '<div class="error-state">Invalid form data provided.</div>';
    }

    try {
      return await this.generateFormHTML(data);
    } catch (error) {
      console.error('FormRendererService: Error generating form HTML', error);
      return '<div class="error-state">Error generating form content.</div>';
    }
  }
}